import { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { TranslatedText } from './TranslatedText';
import { useLanguage } from '../context/LanguageContext';

export const Guestbook = () => {
  const { language } = useLanguage();
  
  const formInitialDetails = {
    nickname: "",
    message: "",
    language: language // Use current language instead of "es"
  };

  const [formDetails, setFormDetails] = useState(formInitialDetails);
  const [messages, setMessages] = useState([]);
  const [buttonText, setButtonText] = useState("Submit");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update form language when app language changes
  useEffect(() => {
    setFormDetails(prevDetails => ({
      ...prevDetails,
      language: language
    }));
  }, [language]);

  // Fetch all messages when component mounts
  useEffect(() => {
    fetchMessages();
  }, []);

  // Function to fetch messages from the backend
  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/guestbook");

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch messages");
      }

      const data = await response.json();
      setMessages(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load messages. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onFormUpdate = (category, value) => {
    setFormDetails({
      ...formDetails,
      [category]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonText("Submitting...");

    if (!formDetails.nickname.trim() || !formDetails.message.trim()) {
      alert("Please fill in both your nickname and message.");
      setButtonText("Submit");
      return;
    }

    try {
      // Ensure language is current
      const dataToSubmit = {
        ...formDetails,
        language: language
      };
      
      const response = await fetch("/api/guestbook", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSubmit),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit message");
      }

      // Get the newly created message from the response
      const newMessage = await response.json();

      // Add the new message to the top of the list
      setMessages([newMessage, ...messages]);

      // Reset the form
      setFormDetails({...formInitialDetails, language: language});
      setError(null);
    } catch (err) {
      console.error("Error submitting message:", err);
      setError("Failed to submit your message. Please try again.");
    } finally {
      setButtonText("Submit");
    }
  };

  const renderMessages = () => {
    return (
      <div>
        {loading && <p><TranslatedText textKey="loadingMessages" /></p>}
        {!loading && messages.length === 0 && (
          <p><TranslatedText textKey="noMessages" /></p>
        )}
        {!loading &&
          messages.length > 0 &&
          messages.map((msg) => (
            <div key={msg.id} className="p-3 mb-3 border rounded">
              <Row>
                <Col md={3}>
                  <h5>{msg.nickname}</h5>
                  <small className="text-muted d-block">
                    {msg.createdAt && new Date(msg.createdAt).toLocaleString()}
                  </small>
                </Col>
                <Col md={9}>
                  <p className="mb-0">{msg.translatedMessage || msg.message}</p>
                </Col>
              </Row>
            </div>
          ))}
      </div>
    );
  };

  return (
    <section className="guestbook" id="guestbook">
      <Container>
        <Row className="align-items-center">
          <Col md={12}>
            <h1><TranslatedText textKey="guestbook" /></h1>
            <h2><TranslatedText textKey="leaveMessage" /></h2>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={5} className="px-1">
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={language === 'en' ? "Enter your nickname" : "输入您的昵称"}
                      value={formDetails.nickname}
                      onChange={(e) => onFormUpdate("nickname", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={7} className="px-1">
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder={language === 'en' ? "Enter your message" : "输入您的留言"}
                      value={formDetails.message}
                      onChange={(e) => onFormUpdate("message", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col sm={12} className="px-1">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={buttonText === "Submitting..."}
                    className="mt-2 px-4"
                    style={{ width: "auto" }}
                  >
                    <span>
                      <TranslatedText textKey={buttonText === "Submit" ? "submit" : "submitting"} />
                    </span>
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <h3><TranslatedText textKey="pastMessages" /></h3>
            {renderMessages()}
          </Col>
        </Row>
      </Container>
    </section>
  );
};